import React, { useEffect, useRef, useState } from "react";
import { Download, Share2, Printer, X, FileText, ZoomIn, ZoomOut, Loader2 } from "lucide-react";
import { Button } from "../ui/button";

const isPhone = () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || "") || window.innerWidth < 768;

// Phones can't scroll/zoom a PDF inside an iframe reliably, so pages are drawn with pdf.js.
function PdfPages({ blob, zoom }) {
  const box = useRef(null);
  const [state, setState] = useState("loading");
  useEffect(() => {
    let off = false;
    (async () => {
      try {
        const pdfjs = await import("pdfjs-dist/build/pdf");
        pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;
        const pdf = await pdfjs.getDocument({ data: new Uint8Array(await blob.arrayBuffer()) }).promise;
        const host = box.current;
        if (off || !host) return;
        host.innerHTML = "";
        const width = (host.clientWidth - 16) * zoom;
        const dpr = Math.min(window.devicePixelRatio || 1, 3);
        for (let n = 1; n <= pdf.numPages; n += 1) {
          const page = await pdf.getPage(n);
          const base = page.getViewport({ scale: 1 });
          const vp = page.getViewport({ scale: (width / base.width) * dpr });
          const c = document.createElement("canvas");
          c.width = vp.width; c.height = vp.height;
          c.style.width = `${width}px`; c.style.height = `${(vp.height / dpr)}px`;
          c.className = "block mx-auto mb-2 bg-white shadow";
          c.dataset.testid = `pdf-viewer-page-${n}`;
          if (off) return;
          host.appendChild(c);
          await page.render({ canvasContext: c.getContext("2d"), viewport: vp }).promise;
        }
        if (!off) setState("ok");
      } catch {
        if (!off) setState("error");
      }
    })();
    return () => { off = true; };
  }, [blob, zoom]);
  return (
    <div className="flex-1 overflow-auto bg-slate-200 p-2" style={{ WebkitOverflowScrolling: "touch" }} data-testid="pdf-viewer-pages">
      {state === "loading" && <div className="flex items-center justify-center gap-2 text-sm text-slate-600 py-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading PDF…</div>}
      {state === "error" && <div className="text-sm text-red-700 text-center py-10">Preview failed — use Download or Share.</div>}
      <div ref={box} />
    </div>
  );
}

function Tool({ onClick, icon: Icon, label, testId, primary }) {
  return (
    <Button size="sm" variant={primary ? "default" : "outline"} onClick={onClick} data-testid={testId}
      className={`h-9 rounded-lg px-2.5 sm:px-3 ${primary ? "bg-red-700 hover:bg-red-800" : ""}`}>
      <Icon className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">{label}</span>
    </Button>
  );
}

// Global in-app PDF viewer (desktop + phones) with Share / Download / Print.
export function PdfViewerHost() {
  const [doc, setDoc] = useState(null);
  const [zoom, setZoom] = useState(1);
  const frame = useRef(null);
  useEffect(() => {
    const on = (e) => { setZoom(1); setDoc(e.detail); };
    window.addEventListener("adams:pdf", on);
    return () => window.removeEventListener("adams:pdf", on);
  }, []);
  useEffect(() => {
    if (!doc) return undefined;
    // Capture Escape first so an invoice dialog underneath stays open.
    const esc = (e) => { if (e.key === "Escape") { e.stopPropagation(); e.preventDefault(); setDoc(null); } };
    window.addEventListener("keydown", esc, true);
    return () => window.removeEventListener("keydown", esc, true);
  }, [doc]);
  if (!doc) return null;
  const phone = isPhone();
  const file = new File([doc.blob], doc.filename, { type: "application/pdf" });
  const canShare = typeof navigator !== "undefined" && !!navigator.canShare?.({ files: [file] });
  const download = () => { const a = document.createElement("a"); a.href = doc.url; a.download = doc.filename; document.body.appendChild(a); a.click(); a.remove(); };
  const share = () => (canShare ? navigator.share({ files: [file], title: doc.filename }).catch(() => {}) : download());
  const print = () => { try { frame.current?.contentWindow?.print(); } catch { window.open(doc.url, "_blank"); } };
  return (
    <div className="fixed inset-0 z-[100] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-0 sm:p-6 animate-in fade-in duration-150" onClick={() => setDoc(null)} onPointerDown={(e) => e.stopPropagation()} style={{ pointerEvents: "auto" }} data-testid="pdf-viewer">
      <div className="bg-white sm:rounded-2xl shadow-2xl w-full max-w-5xl h-full sm:max-h-[94vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 border-b border-slate-200" style={{ paddingTop: "max(0.625rem, env(safe-area-inset-top))" }}>
          <FileText className="h-4 w-4 text-red-700 shrink-0" />
          <div className="font-heading font-bold text-sm truncate flex-1 min-w-0" data-testid="pdf-viewer-title">{doc.filename}</div>
          {phone ? (
            <>
              <Tool onClick={() => setZoom((z) => Math.max(1, z - 0.5))} icon={ZoomOut} label="Zoom out" testId="pdf-viewer-zoom-out" />
              <Tool onClick={() => setZoom((z) => Math.min(3, z + 0.5))} icon={ZoomIn} label="Zoom in" testId="pdf-viewer-zoom-in" />
            </>
          ) : <Tool onClick={print} icon={Printer} label="Print" testId="pdf-viewer-print" />}
          {(canShare || phone) && <Tool onClick={share} icon={Share2} label="Share" testId="pdf-viewer-share" />}
          <Tool onClick={download} icon={Download} label="Download" testId="pdf-viewer-download" primary />
          <button onClick={() => setDoc(null)} className="h-9 w-9 shrink-0 inline-flex items-center justify-center rounded-lg hover:bg-slate-100" aria-label="Close" data-testid="pdf-viewer-close"><X className="h-5 w-5" /></button>
        </div>
        {phone ? <PdfPages blob={doc.blob} zoom={zoom} />
          : <iframe ref={frame} title={doc.filename} src={doc.url} className="flex-1 w-full bg-slate-100" data-testid="pdf-viewer-frame" />}
      </div>
    </div>
  );
}

// Radix can leave `pointer-events:none` on <body> when one dialog closes as another opens.
export function useUnstickBody() {
  useEffect(() => {
    const fix = () => {
      if (document.body.style.pointerEvents === "none" && !document.querySelector('[role="dialog"][data-state="open"],[role="alertdialog"][data-state="open"],[data-radix-popper-content-wrapper]')) {
        document.body.style.pointerEvents = "";
      }
    };
    const mo = new MutationObserver(() => setTimeout(fix, 400));
    mo.observe(document.body, { attributes: true, attributeFilter: ["style"], childList: true });
    return () => mo.disconnect();
  }, []);
}
